/**
 * React Query persistence config for offline mode.
 *
 * Persists the operational query cache (jobs, job details, contacts, calendar,
 * messaging) to IndexedDB so the app can render last-known data while offline.
 * See the offline-mode plan for which keys are intentionally excluded.
 */
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { Query } from '@tanstack/react-query'
import { get, set, del } from 'idb-keyval'
import { BUILTIN_APP_VERSION } from './appVersion'

// Query-key prefixes that are safe + useful to read offline. A query is persisted
// only if its first key segment is in this set.
const PERSISTED_KEY_PREFIXES = new Set<string>([
  // Bootstrap context — without these the app can't get past org/company/permission
  // gating on an offline cold start (shows "no organization").
  'my-organizations',
  'companies',
  'user-features',
  // Operational data readable offline.
  'jobs',
  'job',
  'contacts',
  'contact',
  'calendar',
  'invoices',
  'invoice',
  'estimates',
  'estimate',
  // messaging — mirrored into the cache from MessagingPage (see plan)
  'conversations',
  'messages',
  // Company invoice settings (review link, tax rate, branding) — rarely changes, and the
  // review QR has to render from a cold offline start.
  'company-invoice-settings',
])

// idb-keyval-backed async storage adapter (getItem/setItem/removeItem).
const idbStorage = {
  getItem: (key: string) => get<string>(key).then((v) => v ?? null),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
}

export const queryPersister = createAsyncStoragePersister({
  storage: idbStorage,
  key: 'dispatch-rq-cache',
  // ponytail: throttle disk writes so rapid cache updates don't thrash IndexedDB
  throttleTime: 1000,
})

export const shouldDehydrateQuery = (query: Query): boolean => {
  // Only persist successful queries whose key prefix is allowlisted.
  if (query.state.status !== 'success') return false
  const prefix = query.queryKey?.[0]
  return typeof prefix === 'string' && PERSISTED_KEY_PREFIXES.has(prefix)
}

// Drop persisted cache older than this on rehydrate.
export const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24 * 7 // 7 days

// Bump (via app version) to invalidate stale persisted caches across releases.
export const PERSIST_BUSTER = BUILTIN_APP_VERSION
